import { Mesh, Object3D } from "three";
import { _flagsMap, internalGetNode, HierarchyNode } from "../../hierarchy/common/hierarchyService.js";
import { IHierarchyNode, ITreeProvider, NodeFlags } from "../../hierarchy/common/hierarchy.js";
import { isBatchedMeshlike, isCamera, isInstancedMeshLike, isMaterial, isMeshLike, isObject3D, isRawShaderMaterial, isRenderer, isScene, isShaderMaterial } from "./helper.js";
import { ILogService } from "../../log/common/log.js";
import { Attachment } from "../../../base/common/attachment.js";
import { isGltfLike } from "../common/gltf.js";
import { ServicesAccessor } from "../../instantiation/common/instantiation.js";
import { ActivityAttachment, runTreeAttachmentContributors } from "../../hierarchy/common/attachments.js";
import { CommandService } from "../../commands/common/commandService.js";
import { SetPropertyCommand } from "../../properties/common/propertyCommands.js";
import { handleOf } from "../common/objectHandle.js";
import { buildAddress } from "../../properties/common/propertyPath.js";
import { Needle } from "./propertyReader.js";
import { pollWhileAlive, watchChildHierarchy } from "./childHierarchyWatch.js";



declare module "three" {
    interface Object3D {
        __node?: HierarchyNode;
    }
}


export class ThreeTreeProvider implements ITreeProvider {

    constructor(
        @ILogService private readonly logger: ILogService,
    ) {
    }

    canParse(object: unknown): boolean {
        if (!object) return false;
        if (isObject3D(object)) return true;
        if (isRenderer(object)) return true;
        if (isCamera(object)) return true;
        if (isGltfLike(object)) return true;
        return false;
    }

    async createTree(accessor: ServicesAccessor, object: unknown) {

        if (isGltfLike(object)) {
            object = object.scene || object.scenes[0];
        }


        // TODO: move this into a provider syystem
        // export async function createTree(project: IProjectService, object: import("three").Object3D): Promise<Tree> {

        if (isRenderer(object)) {
            const node = new HierarchyNode({
                object: object,
                id: Math.random().toString(36).substring(2, 9),
                type: "renderer",
                name: object.isWebGLRenderer ? "WebGLRenderer" : "WebGPURenderer",
                parent: null,
                expanded: true,
            });
            node.attachments.push(new ActivityAttachment(node));
            return node;
        }
        // else if (isCamera(object)) {
        //     const node = createNode(object as unknown as Object3D, null);
        //     return node;
        // }
        else if (isObject3D(object)) {
            const root = createNode(object as Object3D, null);
            for (const child of object.children) {
                traverse(child as Object3D, root, 1);
            }
            const disposable = watchChildHierarchy(object as Object3D, root, (obj, r) => {
                for (const child of obj.children) {
                    traverse(child as Object3D, r, 99);
                }
            });
            root.onDidDispose(() => {
                disposable.dispose();
            });
            return root;
        }

        this.logger.error("ThreeTreeProvider: Unsupported object type for creating hierarchy tree");
        return null;


        function traverse(object: Object3D, parentNode: HierarchyNode, level: number) {
            const node = createNode(object, parentNode);
            for (const child of object.children) {
                traverse(child as Object3D, node, level + 1);
            }
            // Publish one complete subtree. Attaching the root first exposed every
            // descendant mutation separately to hierarchy and Svelte listeners.
            parentNode.addChild(node);
        }

        function shouldAutoExpand(object: Object3D): boolean {
            // check if the object is more than 3 level deep
            let level = 0;
            let current: Object3D | null = object;
            while (current?.parent) {
                level++;
                current = current.parent as Object3D;
            }
            return level < 2;
        }

        function createNode(object: Object3D, parent: HierarchyNode | null = null, expanded?: boolean) {
            object.userData ??= {};
            let _node = internalGetNode(object);


            if (!_node) {
                if (expanded === undefined) expanded = shouldAutoExpand(object);

                _node = new HierarchyNode({
                    object: object,
                    id: object.id.toString(),
                    // Render key: numeric three.js ids collide across module
                    // instances (page three vs editor-bundled three, e.g. for
                    // editor-injected helpers) — the uuid doesn't.
                    uuid: typeof object.uuid === "string" ? object.uuid : undefined,
                    type: object.type,
                    name: object.name || object.type,
                    parent: parent,
                    expanded: expanded,
                });
                _node.searchTags?.push(object.type);

                if ((object as any).isBatchedMesh) {
                    _node.icon = "batchedMesh";
                    _node.searchTags?.push("batchedMesh");
                }
                else if ((object as any).isInstancedMesh) {
                    _node.icon = "batchedMesh";
                    _node.searchTags?.push("instancedMesh");
                }

                const node = _node;

                // Listen to three.js events to update the hierarchy. NAMED and
                // REMOVED on dispose: these closures live on the OBJECT — after
                // the node is disposed (deferred disposal of a removed node) a
                // re-add of the same object would otherwise fire the STALE
                // "added" listener and attach the dead node next to the fresh
                // one the parent's "childadded" creates (the create-undo-redo
                // duplicate).
                const onChildAdded = (evt: { child?: unknown }) => {
                    if (node.isDisposed) return;
                    const child = evt.child as import("three").Object3D;
                    const node_def = createNode(child, node);
                    for (const _child of child.children) {
                        traverse(_child as Object3D, node_def, 99);
                    }
                    node.addChild(node_def);
                };
                const onAdded = () => {
                    if (node.isDisposed) return;
                    const parentNode = createNode(object.parent as Object3D);
                    parentNode.addChild(node);
                };
                const onRemoved = () => {
                    const parent = node.parent;
                    parent?.removeChild(node);
                };
                object.addEventListener("childadded", onChildAdded as never);
                object.addEventListener("added", onAdded);
                object.addEventListener("removed", onRemoved);

                const disposable = watchChildHierarchy(object, _node, (obj, n) => {
                    if (n.parent) traverse(obj, n.parent, 99);
                });
                _node.onDidDispose(() => {
                    disposable.dispose();
                    object.removeEventListener("childadded", onChildAdded as never);
                    object.removeEventListener("added", onAdded);
                    object.removeEventListener("removed", onRemoved);
                });


                // Poll for visibility/name changes three.js doesn't event. Weak
                // (see pollWhileAlive): this timer must never pin object/node.
                let wasVisible = object.visible;
                const visibilityWatch = pollWhileAlive(object, node, 300, (obj, n) => {
                    if (n.isDisposed) return;
                    if (obj.visible !== wasVisible) {
                        wasVisible = obj.visible;
                        n.notifyChanged();
                    }
                    updateName(n, obj);
                });
                _node.onDidDispose(() => visibilityWatch.dispose());

                const meshLike = isMeshLike(object);
                // params (not closure) so the visibility poll's tick never
                // strongly captures node/object — that would defeat the weak poll.
                function updateName(node: HierarchyNode, object: Object3D) {
                    node.name = object.name;

                    let newLabel = object.name || object.type;
                    // if the object does not have a unique name we append the material name (if any)
                    if (!object.name) {
                        let namePostfix = "";
                        if (meshLike && "material" in object) {
                            const material = object["material"];
                            if (isMaterial(material) && material.name) {
                                let materialName = material.name;
                                namePostfix = ` (${materialName})`;
                            }
                        }
                        else if (isScene(object)) {
                            if (object.children.length === 1 && object.children[0].children?.length === 0) {
                                const onlyChild = object.children[0];
                                if (isMeshLike(onlyChild) && "material" in onlyChild) {
                                    if (isShaderMaterial(onlyChild.material) || isRawShaderMaterial(onlyChild.material)) {
                                        namePostfix = ` (Postprocessing)`;
                                    }
                                }
                            }
                        }
                        if (namePostfix.length === 0) {
                            namePostfix = `_${object.id}`;
                        }

                        newLabel += namePostfix;
                    }

                    node.label = newLabel;
                }

                node.attachments.push(new VisibilityAttachment(node, object));

                if (meshLike) {
                    node.attachments.push(new InstancingInfoAttachment(node, object as Mesh));
                    node.attachments.push(new HeavyAttachment(node, object));
                }

                // COMPONENTS badge — follows the object for the node's whole life:
                // a component can be added or removed at any time, and both
                // commands notify the node on every path (including undo/redo)
                const componentsBadge = attachComponentsBadge(node, object);
                node.onDidDispose(() => componentsBadge.dispose());

                // START CAMERA badge — follows the GAMEOBJECT DATA (userData.tag ===
                // "MainCamera", the engine's own start-camera rule; the tag arrives
                // live via Set as Start Camera, or from authored files through
                // NEEDLE_gameobject_data). Camera nodes get the attachment so it can
                // APPEAR when they are tagged later; other nodes only when already
                // tagged — one attachment per row costs a component mount, so it is
                // not registered everywhere.
                if (isCamera(object) || hasCameraComponent(object)
                    || (object.userData as { tag?: string } | undefined)?.tag === "MainCamera") {
                    node.attachments.push(new StartCameraAttachment(node, object));
                }

                // NOT for editor furniture (Editor Camera, Editmode Scene —
                // HideInHierarchy): their badge is never visible, but its
                // active/inactive flip-flop fired a node notification + parenting
                // check every few frames AT IDLE (the 2026-07-28 log spam)
                if ((isCamera(object) || isScene(object)) && !HierarchyNode.hasFlag(node, NodeFlags.HideInHierarchy)) {
                    _node.attachments.push(new ActivityAttachment(_node));
                }

                // higher-layer badges (editor: cloud-asset roots) — attachments
                // only survive when added at node CREATION, so this is the seam
                runTreeAttachmentContributors(node, object);


                // Only editor-side parent changes need deferred synchronization back to three.js.
                let parentingCheckIsScheduled = false;
                const checkParenting = () => {
                    if (!parentingCheckIsScheduled) return;
                    parentingCheckIsScheduled = false;
                    if (!node || (node as { isDisposed?: boolean }).isDisposed) return;
                    // A PARENTLESS node is NEVER a reason to mutate the three graph:
                    // panel deletions remove the three object through their COMMANDS
                    // (this branch was redundant — the comment above always doubted
                    // it), while node teardown (playmode scene swap, project replace,
                    // file close) leaves nodes parentless-but-alive for a grace
                    // window (_scheduleDisposeIfDetached) — and acting in that window
                    // ripped LIVE objects out: entering play removed the CAMERA from
                    // the play scene → frozen view, "stuck" OrbitControls.
                    if (!node?.parent) {
                        // nothing to sync — permanent roots (the edit scene) land here on
                        // EVERY notification, so this branch stays silent (the old debug
                        // log flooded the console; it served its diagnostic purpose)
                    }
                    // Add the object3d to the correct parent if needed
                    else {
                        const obj = node!.owner as import("three").Object3D;
                        const parent = node!.parent?.owner as import("three").Object3D;
                        if (obj.parent !== parent) {
                            parent.add(obj);
                            // make sure index in three.js matches
                            const currentIndex = parent.children.indexOf(obj);
                            const desiredIndex = parent.children.indexOf((node!.parent as HierarchyNode).owner as import("three").Object3D);
                            if (currentIndex !== desiredIndex) {
                                parent.children.splice(currentIndex, 1);
                                parent.children.splice(desiredIndex, 0, obj);
                            }
                        }
                    }
                }

                // Listen to node events to update three.js object
                // IMPORTANT: We do this at the end because we don't want to receive did change events on the node triggered by any of the above code (e.g. setting the flags in ActivityAttachment triggers the CubeCamera to be removed: https://bruno-simon.com/)
                node.onDidChange(({ node: _node }) => {
                    if (_node === node && node.parent && object.parent !== node.parent.owner) {
                        if (!parentingCheckIsScheduled) {
                            // sync parenting on next tick
                            parentingCheckIsScheduled = true;
                            setTimeout(checkParenting, 0);
                        }
                    }

                    // HACK: sync node name
                    updateName(node, object);
                });


            }

            return _node;
        }
    }
}


class VisibilityAttachment extends Attachment {

    constructor(node: IHierarchyNode, object: Object3D) {

        const visibleIcon = "visible";
        const hiddenIcon = "invisible";

        super(
            "visibility",
            undefined,
            "Click to toggle visibility",
            node.visible ? visibleIcon : hiddenIcon,
            () => {
                this._name = (object.name || object.type) + "_" + object.id;
                CommandService.get().executeCommand<SetPropertyCommand>(SetPropertyCommand.ID, {
                    address: buildAddress(handleOf(node) ?? node.id, ["visible"]),
                    value: !object.visible,
                    previousValue: object.visible,
                });
            }
        )

        this._name = (object.name || object.type) + "_" + object.id;

        this._register(node.onDidChange(() => {
            this.icon = node.visible ? visibleIcon : hiddenIcon;
        }));
    }
}

class HeavyAttachment extends Attachment {

    constructor(node: IHierarchyNode, object: Mesh) {
        super(
            "heavy",
            undefined,
            `Mesh with more than 10,000 vertices`,
            "icon-heavy",
        );
        this._name = (object.name || object.type) + "_" + object.id;
        const threshold = 10_000;
        this.visible = object.geometry?.attributes?.position?.count! > threshold;
        // Write ONLY on an actual change. Both paths below run for EVERY mesh
        // node, the interval once a second — so assigning unconditionally
        // invalidated reactive state for the whole tree every tick and dragged
        // the UI through a full flush. On a heavy page that showed up as a ~350ms
        // timer task and a storm of querySelector work (next.junni.co.jp,
        // 2026-07-28). Vertex counts essentially never change; the poll only
        // exists to catch a geometry swap that doesn't raise onDidChange.
        let lastVertices = -1;
        // object/self are PARAMS (not closure) so the poll below never strongly
        // captures the mesh or this attachment — that timer would otherwise pin
        // the whole subtree (materials/textures) until the node is disposed.
        const sync = (object: Mesh, self: HeavyAttachment) => {
            const vertices = object.geometry?.attributes?.position?.count || 0;
            if (vertices === lastVertices) return;
            lastVertices = vertices;
            self.description = `This mesh has ${vertices.toLocaleString()} vertices`;
            self.visible = vertices > threshold;
        };
        sync(object, this);
        this._register(node.onDidChange(() => sync(object, this)));
        this._register(pollWhileAlive(object, this, 1000, sync));
    }
}

class HasComponentsAttachment extends Attachment {
    constructor(node: IHierarchyNode, object: Object3D) {
        super(
            "components",
            undefined,
            `Object has components attached`,
            "component",
        );
    }
}

/**
 * Keep the components badge in step with the object, for as long as the node
 * lives. Deciding it once at node creation left a freshly added component
 * unmarked and a removed one still badged.
 *
 * The badge is CREATED AND DESTROYED rather than kept around hidden: an
 * attachment costs a Svelte component mount on its row even while invisible,
 * and most rows in a scene never host a component. That is also why the
 * listener is registered here at node creation — it runs before any tree row
 * subscribes, so the list is already correct by the time the row re-renders.
 */
export function attachComponentsBadge(node: IHierarchyNode, object: Object3D): { dispose(): void } {
    let badge: HasComponentsAttachment | null = null;

    const sync = () => {
        const hasComponents = Needle.objectHasComponents(object)?.hasComponents === true;
        if (hasComponents === !!badge) return;
        if (hasComponents) {
            badge = new HasComponentsAttachment(node, object);
            node.attachments.push(badge);
        }
        else if (badge) {
            const index = node.attachments.indexOf(badge);
            if (index >= 0) node.attachments.splice(index, 1);
            badge.dispose();
            badge = null;
        }
    };

    sync();
    const listener = node.onDidChange(sync);
    return { dispose: () => listener.dispose() };
}

/** an engine Camera COMPONENT hangs off the object (only it carries isCamera on a component) */
export function hasCameraComponent(object: object): boolean {
    const components = (object as { userData?: { components?: unknown[] } }).userData?.components;
    return Array.isArray(components) && components.some(c => !!c && typeof c === "object" && (c as { isCamera?: boolean }).isCamera === true);
}

/** START CAMERA badge — shown while the node is tagged MainCamera ("Set as Start
 * Camera"): the deployed scene renders from here. Tag edits notify the node
 * (the command calls notifyChanged), which re-derives visibility. */
class StartCameraAttachment extends Attachment {
    constructor(node: IHierarchyNode, object: Object3D) {
        super(
            "startCamera",
            undefined,
            "Start camera — the scene starts rendering from this camera",
            "camera",
        );
        const update = () => {
            this.visible = (object.userData as { tag?: string } | undefined)?.tag === "MainCamera";
        };
        update();
        this._register(node.onDidChange(update));
    }
}


class InstancingInfoAttachment extends Attachment {

    constructor(node: IHierarchyNode, object: Mesh) {
        super(
            "instancingInfo",
            undefined,
            undefined,
            undefined,
        );
        this.visible = isInstancedMeshLike(object) || isBatchedMeshlike(object);
        // object/self are PARAMS so this poll never strongly pins the mesh or the
        // attachment (weak — see pollWhileAlive).
        this._register(pollWhileAlive(object, this, 1000, (object, self) => {
            if (isInstancedMeshLike(object)) {
                self.visible = true;
                const count = object.count;
                self.label = String(count);
                self.description = `InstancedMesh with ${count} instances`;
            }
            else if (isBatchedMeshlike(object) && object.instanceCount !== undefined) {
                self.visible = true;
                self.label = object.instanceCount !== undefined ? `${object.instanceCount?.toLocaleString()}` : "";///${object.maxInstanceCount}`;
                const maxInstanceCount = object.maxInstanceCount ?? 0;
                self.description = `BatchedMesh with ${(object.instanceCount ?? "?")?.toLocaleString()} / ${maxInstanceCount?.toLocaleString()} instances (${(maxInstanceCount - (object.instanceCount ?? "?"))?.toLocaleString()} free)`;
            }
            else self.visible = false;
        }))
    }
}
